import { clear, createStore, get, getMany, keys, set, setMany } from 'idb-keyval';

const store = createStore('a1-recordings', 'chunks');

const CONTEXT_KEY = 'context';
const DURATION_KEY = 'duration';
const CHUNK_PREFIX = 'chunk/';

/** What the recording was for, so a recovered buffer is only offered back in the same place. */
export interface RecordingContext {
  recordingId: string;
  startedAt: number;
  mimeType: string;
  extension: string;
  respondentId: string | null;
  mode: string;
  dictation: boolean;
}

const chunkKey = (sequence: number): string => `${CHUNK_PREFIX}${String(sequence).padStart(6, '0')}`;

const chunkKeys = async (): Promise<string[]> =>
  (await keys<string>(store)).filter((key) => typeof key === 'string' && key.startsWith(CHUNK_PREFIX)).sort();

export const beginBuffer = async (context: RecordingContext): Promise<void> => {
  await clearBuffer();
  await set(CONTEXT_KEY, context, store);
};

/** One key per chunk, because rewriting a growing array would be quadratic over an hour of audio. */
export const appendChunk = (sequence: number, chunk: Blob, recordedMs: number): Promise<void> =>
  setMany(
    [
      [chunkKey(sequence), chunk],
      [DURATION_KEY, recordedMs]
    ],
    store
  );

export interface BufferedRecording {
  context: RecordingContext;
  blob: Blob;
  durationMs: number;
}

export const readBufferedContext = (): Promise<RecordingContext | undefined> => get<RecordingContext>(CONTEXT_KEY, store);

const readBufferedBlob = async (mimeType: string): Promise<Blob | null> => {
  const orderedKeys = await chunkKeys();
  if (orderedKeys.length === 0) return null;

  const chunks = (await getMany<Blob>(orderedKeys, store)).filter((chunk): chunk is Blob => chunk instanceof Blob);
  if (chunks.length === 0) return null;

  return new Blob(chunks, { type: mimeType });
};

/** Null unless there is buffered audio. */
export const readBufferedRecording = async (): Promise<BufferedRecording | null> => {
  const context = await readBufferedContext();
  if (!context) return null;

  const [blob, durationMs] = await Promise.all([readBufferedBlob(context.mimeType), get<number>(DURATION_KEY, store)]);
  if (!blob || blob.size === 0) return null;

  return { context, blob, durationMs: durationMs ?? 0 };
};

export const clearBuffer = (): Promise<void> => clear(store);
